/**
 * Отказы по демо-доступу (CAB-670). Не ошибка системы, а состояние: у клиента в
 * демо-доступе часть действий недоступна, пока он не подключит своих поставщиков.
 *
 * Два источника, оба со своей формой:
 *
 * * *router* запрещает группы запросов триал-клиенту и отвечает {{403}} с маркером
 *   {{trial_restricted}} (SER-1350);
 * * *MH* отказывает на оформлении заказа кодом {{error_code: 461}} (SER-1320).
 *   Причина запрета не формальная: заказ в демо-режиме ушёл бы на счёт поставщика
 *   партнёра, чьи креды подставлены для показа. Своих у клиента ещё нет.
 *
 * Прикладные коды приезжают из-за шлюза по-разному: настоящим HTTP-статусом, полем
 * {{error.code}} конверта ЛК, либо завёрнутыми в текст сообщения как
 * {{Data: {"error_code":461}}}. Поэтому проверяем все места — так же, как это уже
 * делает разбор истёкшего токена в apiJwt.
 */

import axios from "axios";

/** Код MH: оформление заказа недоступно в демо-режиме. */
export const TRIAL_RESTRICTED_CODE = 461;

/** Маркер router: запрос запрещён триал-клиенту. */
export const TRIAL_RESTRICTED_MARKER = "trial_restricted";

const CODE_IN_TEXT = /"error_code"\s*:\s*461\b/;

function textCarriesRefusal(text: unknown): boolean {
  if (typeof text !== "string" || text.length === 0) return false;
  return text.includes(TRIAL_RESTRICTED_MARKER) || CODE_IN_TEXT.test(text);
}

interface RefusalEnvelope {
  error?: { code?: number; message?: string } | string;
  error_code?: number;
  err?: string;
}

function envelopeCarriesRefusal(data: unknown): boolean {
  if (!data || typeof data !== "object") return textCarriesRefusal(data);

  const envelope = data as RefusalEnvelope;

  if (envelope.error_code === TRIAL_RESTRICTED_CODE) return true;
  if (textCarriesRefusal(envelope.err)) return true;

  if (typeof envelope.error === "string") return textCarriesRefusal(envelope.error);
  if (envelope.error?.code === TRIAL_RESTRICTED_CODE) return true;
  if (textCarriesRefusal(envelope.error?.message)) return true;

  return false;
}

/** Отказ по демо-доступу в ошибке запроса — HTTP-статусом, кодом конверта или текстом. */
export function isTrialRestrictedError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === TRIAL_RESTRICTED_CODE) return true;
    return envelopeCarriesRefusal(error.response?.data);
  }

  return envelopeCarriesRefusal(error);
}

/**
 * Отказ по демо-доступу в пер-позиционной ошибке заказа: {{make_order}} отвечает
 * 200, а причину отказа кладёт в каждую позицию отдельно.
 */
export function isTrialRestrictedItemError(itemError: unknown): boolean {
  return textCarriesRefusal(itemError);
}

/** Есть ли отказ по демо-доступу хотя бы в одной позиции заказа. */
export function hasTrialRestrictedItem(errors: Array<{ error: string }>): boolean {
  return errors.some((entry) => isTrialRestrictedItemError(entry.error));
}
